import {
  DashboardApprovalDocumentItem,
  DashboardCard,
  DashboardCardFilters,
  DashboardFilterValue,
  DashboardResponse,
  DashboardTaskItem,
  Document,
  DocumentVersionApproverStatus,
} from "@models";
import { DocumentStatus } from "@types";
import type { UnifiedTask } from "src/services";
import {
  getLatestDocumentStatus,
  getLatestDocumentVersion,
} from "src/utils/documents";
import { getDashboardTaskItems } from "./dashboardCardData";

type DeriveDashboardDataParams = {
  dashboard: DashboardResponse;
  tasks?: UnifiedTask[];
  documents?: Document[];
  selectedDeviceId?: string;
};

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toDate = (value: Date | string | null | undefined) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

const isFilterObjectValue = (
  value: DashboardFilterValue | undefined
): value is Exclude<DashboardFilterValue, string[]> =>
  Boolean(value) && !Array.isArray(value) && typeof value === "object";

const hasActiveFilterValue = (value: DashboardFilterValue | undefined) => {
  if (!value) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Object.values(value).some((entry) =>
    Array.isArray(entry) ? entry.length > 0 : Boolean(entry)
  );
};

const hasActiveFilters = (filters: DashboardCardFilters) =>
  Object.values(filters).some(hasActiveFilterValue);

const getUserIds = (value: DashboardFilterValue | undefined) => {
  if (!isFilterObjectValue(value) || !("userIds" in value)) {
    return [];
  }

  return value.userIds;
};

const getDateFilter = (value: DashboardFilterValue | undefined) => {
  if (!isFilterObjectValue(value) || "userIds" in value) {
    return undefined;
  }

  return value;
};

const isWithinDateFilter = (
  value: Date | string | null | undefined,
  filter: ReturnType<typeof getDateFilter>
) => {
  if (!filter) {
    return true;
  }

  const date = toDate(value);

  if (filter.preset === "no_due_date") {
    return date === null;
  }

  if (!date) {
    return false;
  }

  const todayStart = startOfToday();
  const todayEnd = endOfDay(todayStart);

  if (filter.preset === "overdue") {
    return date < todayStart;
  }

  if (filter.preset === "today") {
    return date >= todayStart && date <= todayEnd;
  }

  if (filter.preset === "next_7_days") {
    return date >= todayStart && date <= endOfDay(addDays(todayStart, 7));
  }

  const from = filter.from ? new Date(filter.from) : null;
  const to = filter.to ? endOfDay(new Date(filter.to)) : null;

  if (from && date < from) {
    return false;
  }

  if (to && date > to) {
    return false;
  }

  return true;
};

const matchesTaskDeviceFilter = (task: UnifiedTask, deviceId?: string) => {
  if (!deviceId) {
    return true;
  }

  return task.device?.id === deviceId || task.device === null;
};

const matchesTaskDueDateFilter = (
  task: UnifiedTask,
  filters: DashboardCardFilters
) => isWithinDateFilter(task.dueDate, getDateFilter(filters["task-due-date"]));

const matchesTaskStatusFilter = (
  task: UnifiedTask,
  filters: DashboardCardFilters
) => {
  const filter = filters["task-status"];
  if (Array.isArray(filter) && filter.length > 0) {
    return filter.includes(task.status);
  }

  return (
    task.status !== "DONE" &&
    task.docStatus !== DocumentStatus.LIVE &&
    task.docStatus !== DocumentStatus.ARCHIVED
  );
};

const matchesTaskDocumentStatusFilter = (
  task: UnifiedTask,
  filters: DashboardCardFilters
) => {
  const filter = filters["document-status"];
  if (!Array.isArray(filter) || filter.length === 0) {
    return true;
  }

  return task.docStatus ? filter.includes(task.docStatus) : false;
};

const matchesTaskAssigneeFilter = (
  task: UnifiedTask,
  filters: DashboardCardFilters
) => {
  const filter = filters["tasks-assignee"];
  if (!isFilterObjectValue(filter) || !("userIds" in filter)) {
    return true;
  }

  const includeUnassigned =
    "includeUnassigned" in filter && filter.includeUnassigned === true;
  if (filter.userIds.length === 0 && !includeUnassigned) {
    return true;
  }

  return (
    (task.assignee ? filter.userIds.includes(task.assignee.id) : false) ||
    (includeUnassigned && task.assignee === null)
  );
};

const buildTaskItem = (task: UnifiedTask): DashboardTaskItem => ({
  ...task,
  type: "task",
});

const filterDashboardTasks = ({
  tasks,
  filters,
  deviceId,
}: {
  tasks: UnifiedTask[];
  filters: DashboardCardFilters;
  deviceId?: string;
}) =>
  tasks
    .filter(
      (task) =>
        matchesTaskDeviceFilter(task, deviceId) &&
        matchesTaskStatusFilter(task, filters) &&
        matchesTaskDocumentStatusFilter(task, filters) &&
        matchesTaskAssigneeFilter(task, filters) &&
        matchesTaskDueDateFilter(task, filters)
    )
    .map(buildTaskItem);

const isUpcomingDocumentTask = (task: UnifiedTask) =>
  task.taskType === "DOCUMENT" &&
  task.status !== "DONE" &&
  task.docStatus !== DocumentStatus.LIVE &&
  task.docStatus !== DocumentStatus.ARCHIVED &&
  (task.assignee !== null || task.dueDate !== null);

const getTaskDueDateSortTime = (task: DashboardTaskItem) => {
  const date = toDate(task.dueDate);
  return date?.getTime() ?? Number.POSITIVE_INFINITY;
};

const sortTasksByDueDate = (tasks: DashboardTaskItem[]) =>
  [...tasks].sort((firstTask, secondTask) => {
    const dueDateDelta =
      getTaskDueDateSortTime(firstTask) - getTaskDueDateSortTime(secondTask);

    if (dueDateDelta !== 0) {
      return dueDateDelta;
    }

    if (firstTask.title !== secondTask.title) {
      return firstTask.title.localeCompare(secondTask.title);
    }

    return firstTask.id.localeCompare(secondTask.id);
  });

const dedupeTasksByPrecedence = (
  taskGroups: DashboardTaskItem[][]
): DashboardTaskItem[] => {
  const seenTaskIds = new Set<string>();
  const visibleTasks: DashboardTaskItem[] = [];

  taskGroups.forEach((tasks) => {
    sortTasksByDueDate(tasks).forEach((task) => {
      if (seenTaskIds.has(task.id)) {
        return;
      }

      seenTaskIds.add(task.id);
      visibleTasks.push(task);
    });
  });

  return visibleTasks;
};

const buildOutdatedDueSoonTasks = ({
  tasks,
  filters,
  deviceId,
}: {
  tasks: UnifiedTask[];
  filters: DashboardCardFilters;
  deviceId?: string;
}) => {
  const overdueTasks = filterDashboardTasks({
    tasks: tasks.filter((task) =>
      isWithinDateFilter(task.dueDate, { preset: "overdue" })
    ),
    filters,
    deviceId,
  });
  const dueInNextSevenDaysTasks = filterDashboardTasks({
    tasks: tasks.filter((task) =>
      isWithinDateFilter(task.dueDate, { preset: "next_7_days" })
    ),
    filters,
    deviceId,
  });
  const upcomingDocumentTasks = filterDashboardTasks({
    tasks: tasks.filter(isUpcomingDocumentTask),
    filters,
    deviceId,
  });

  return dedupeTasksByPrecedence([
    overdueTasks,
    dueInNextSevenDaysTasks,
    upcomingDocumentTasks,
  ]);
};

const matchesDocumentDeviceFilter = (document: Document, deviceId?: string) => {
  if (!deviceId) {
    return true;
  }

  return document.deviceId === deviceId || document.deviceId === null;
};

const isUnapprovedDocument = (document: Document) => {
  const latestVersion = getLatestDocumentVersion(document);
  return Boolean(
    latestVersion?.readyForApproval && latestVersion.revision === null
  );
};

const isWaitingForApproval = (document: Document) => {
  const latestVersion = getLatestDocumentVersion(document);
  const approvers = latestVersion?.documentVersionApprover ?? [];

  return (
    approvers.length > 0 &&
    approvers.some(
      (approver) =>
        approver.approvalStatus !== DocumentVersionApproverStatus.APPROVED
    )
  );
};

const matchesApprovalDocumentFilters = (
  document: Document,
  filters: DashboardCardFilters,
  deviceId?: string
) => {
  if (!matchesDocumentDeviceFilter(document, deviceId)) {
    return false;
  }

  const latestVersion = getLatestDocumentVersion(document);
  if (!latestVersion) {
    return false;
  }

  const authorIds = getUserIds(filters["document-author"]);
  const approverIds = getUserIds(filters["document-approver"]);

  if (authorIds.length > 0 && !authorIds.includes(latestVersion.createdBy)) {
    return false;
  }

  if (
    approverIds.length > 0 &&
    !latestVersion.documentVersionApprover.some((approver) =>
      approver.userId ? approverIds.includes(approver.userId) : false
    )
  ) {
    return false;
  }

  return true;
};

const buildApprovalDocumentItem = (
  document: Document
): DashboardApprovalDocumentItem | null => {
  const latestVersion = getLatestDocumentVersion(document);
  if (!latestVersion) {
    return null;
  }

  return {
    type: "approval-document",
    documentId: document.id,
    title: document.title,
    templateKey: document.templateKey,
    deviceId: document.deviceId ?? null,
    status: getLatestDocumentStatus(document),
    latestVersionId: latestVersion.id,
    approvers: latestVersion.documentVersionApprover.map((approver) => ({
      id: approver.id,
      userId: approver.userId ?? null,
      approvalStatus: approver.approvalStatus ?? null,
      updatedAt: approver.updatedAt.toISOString(),
      user: {
        id: approver.user.id,
        email: approver.user.email,
        firstName: approver.user.firstName ?? null,
        lastName: approver.user.lastName ?? null,
      },
    })),
  };
};

const buildWaitingApprovalDocuments = ({
  documents,
  filters,
  deviceId,
}: {
  documents: Document[];
  filters: DashboardCardFilters;
  deviceId?: string;
}) =>
  documents
    .filter(
      (document) =>
        matchesApprovalDocumentFilters(document, filters, deviceId) &&
        isUnapprovedDocument(document) &&
        isWaitingForApproval(document)
    )
    .map(buildApprovalDocumentItem)
    .filter(
      (document): document is DashboardApprovalDocumentItem => document !== null
    )
    .sort((a, b) => {
      const titleCompare = a.title.localeCompare(b.title);
      if (titleCompare !== 0) {
        return titleCompare;
      }

      return a.documentId.localeCompare(b.documentId);
    });

const deriveDashboardCard = ({
  card,
  tasks,
  documents,
  selectedDeviceId,
}: Omit<DeriveDashboardDataParams, "dashboard"> & {
  card: DashboardCard;
}): DashboardCard => {
  if (card.key === "open-tasks") {
    const shouldUseSupportTasks =
      tasks !== undefined &&
      (tasks.length > 0 || hasActiveFilters(card.filters));
    const items = filterDashboardTasks({
      tasks: shouldUseSupportTasks ? tasks : getDashboardTaskItems(card),
      filters: card.filters,
      deviceId: selectedDeviceId,
    });

    return {
      ...card,
      count: items.length,
      items,
    };
  }

  if (card.key === "outdated-due-soon") {
    const shouldUseSupportTasks =
      tasks !== undefined &&
      (tasks.length > 0 || hasActiveFilters(card.filters));
    const items = buildOutdatedDueSoonTasks({
      tasks: shouldUseSupportTasks ? tasks : getDashboardTaskItems(card),
      filters: card.filters,
      deviceId: selectedDeviceId,
    });

    return {
      ...card,
      count: items.length,
      items,
    };
  }

  if (card.key === "documents-currently-routing") {
    const shouldUseSupportDocuments =
      documents !== undefined &&
      (documents.length > 0 || hasActiveFilters(card.filters));

    if (!shouldUseSupportDocuments) {
      return card;
    }

    const items = buildWaitingApprovalDocuments({
      documents,
      filters: card.filters,
      deviceId: selectedDeviceId,
    });

    return {
      ...card,
      count: items.length,
      items,
    };
  }

  return card;
};

export const deriveDashboardData = ({
  dashboard,
  tasks,
  documents,
  selectedDeviceId,
}: DeriveDashboardDataParams): DashboardResponse => ({
  ...dashboard,
  cards: dashboard.cards.map((card) =>
    deriveDashboardCard({
      card,
      tasks,
      documents,
      selectedDeviceId,
    })
  ),
});


